import { AxiosResponse } from 'axios'
import { HttpService } from '@/services/common/HttpService'
import { TableViewParameters, TableViewResult } from '@/models/TableView'
import { Charge, Lead } from '@/models/dto'
import { TwilioSyncAccessToken } from '@/models/dto'

const httpService: HttpService = new HttpService()

/**
 * A playback session from coachrail-call. STREAM: a short-lived, signed stream URL (served by
 * coachrail-call; never api.twilio.com). EXTERNAL: a provider link not proxied yet (Dialpad).
 */
export interface RecordingPlaybackSession {
  type: 'STREAM' | 'EXTERNAL'
  url: string
  expiresAt: string | null
}

export default {
  getCalls(params) {
    const { sorts, filters, pageSize = 10, page = 1 } = params

    let query = `page=${page}&pageSize=${pageSize}`
    if (sorts) {
      query += `&${sorts}`
    }
    if (filters) {
      query += `&${filters}`
    }
    query = encodeURI(query)
    const url = `/tables/calls?${query}`
    return httpService.get(url)
  },
  markCallReturned(callId) {
    const url = `/calls/returned/${callId}`
    return httpService.get(url)
  },
  getCallById(callId) {
    const url = `/calls/${callId}`
    return httpService.get(url)
  },
  createRecordingSession(callId: number, download = false): Promise<AxiosResponse<RecordingPlaybackSession>> {
    const url = `/calls/${callId}/recording/session${download ? '?download=true' : ''}`
    return httpService.post(url, null)
  },
  editCallNote(callNote) {
    const url = `/calls/editNote`
    return httpService.post(url, callNote)
  },
  addCallNote(callNote) {
    const url = `/calls/addNote`
    return httpService.post(url, callNote)
  },
  getCallReasonTypes() {
    const url = `/calls/reasonTypes`
    return httpService.get(url)
  },
  getCallCompleteReasonTypes() {
    const url = `/calls/callCompleteReasonTypes`
    return httpService.get(url)
  },
  getCallSatisfactionTypes() {
    const url = `/calls/satisfactionTypes`
    return httpService.get(url)
  },
  setLeadOnCall(payload) {
    const url = `/calls/setLead`
    return httpService.post(url, payload)
  },
  completeCall(payload) {
    const url = `/calls/complete`
    return httpService.post(url, payload)
  },
  getCallIdByCallSid(params) {
    const url = `/calls/callId/${params.callSid}`
    return httpService.get(url)
  },
  getLastCompletedCallByPhoneNumber(phone) {
    const url = `/calls/phone/${phone}`
    return httpService.get(url)
  },
  callsTableExport(params) {
    const { sorts, filters } = params

    let query = ''
    if (sorts) {
      query += query.length ? `&${sorts}` : `${sorts}`
    }
    if (filters) {
      query += query.length ? `&${filters}` : `${filters}`
    }
    query = encodeURI(query)
    const url = `/tables/calls/export?${query}`
    return httpService.get(url, { responseType: 'blob' })
  },

  getTwilioSyncAccessToken(): Promise<AxiosResponse<TwilioSyncAccessToken>> {
    const url = `/twilio/sync/token`
    return httpService.get(url)
  },

  getDialpadWebsocketUrl(): Promise<AxiosResponse<{ websocketUrl: string }>> {
    return httpService.get('/dialpad/websocket/url')
  },

  getDialpadCurrentCall(agentEmail: string): Promise<AxiosResponse<{
    callId: string | null
    state: string | null
    direction: string | null
    callerPhone: string | null
    callerName: string | null
    brandPhone: string | null
    brandName: string | null
    brandCallerId: string | null
    parentBrandName: string | null
    currentQueue: string | null
    agentStatus: string | null
  }>> {
    return httpService.get(`/dialpad/current-call?agentEmail=${encodeURIComponent(agentEmail)}`)
  },

  getDialpadCallCenterNames(): Promise<AxiosResponse<{ names: Record<string, string> }>> {
    return httpService.get('/dialpad/callcenters/names')
  },
}
